import { Point } from '../../types/Point';
import { Position2D, SelectionRect } from '../types/eventTypes';

/**
 * Captures the area behind the point from the PDF canvas
 * @param pdfContainer - Canvas element containing the PDF
 * @param cursorPosition - Current cursor position
 * @param size - Size of the capture area
 * @returns Promise containing data URL of captured image
 */
export const capturePDFArea = async (
  pdfContainer: HTMLCanvasElement,
  cursorPosition: Position2D,
  size: number
): Promise<string> => {
  if (!pdfContainer || !cursorPosition) return '';

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = size;
  canvas.height = size;

  // Calculate the scaling factor based on the relationship between the canvas size and the PDF dimensions
  const scaleX = pdfContainer.width / pdfContainer.clientWidth;
  const scaleY = pdfContainer.height / pdfContainer.clientHeight;

  // Adjust the capture coordinates based on the scaling factors
  const captureX = cursorPosition.x * scaleX - size / 2;
  const captureY = cursorPosition.y * scaleY - size / 2;

  if (context) {
    context.drawImage(pdfContainer, captureX, captureY, size, size, 0, 0, size, size);
    return canvas.toDataURL('image/png', 1.0);
  }
  return '';
};

/**
 * Calculates the center coordinates of a set of points
 * @param points - Array of points to calculate center for
 * @returns Position object with x,y coordinates of center
 */
export const calculateCenterOfPoints = (points: Point[]): Position2D => {
  if (!points || points.length === 0) return { x: 0, y: 0 };

  // Calculate bounding box
  const minX = Math.min(...points.map(point => point.x));
  const minY = Math.min(...points.map(point => point.y));
  const maxX = Math.max(...points.map(point => point.x));
  const maxY = Math.max(...points.map(point => point.y));

  // Calculate center
  return {
    x: minX + (maxX - minX) / 2,
    y: minY + (maxY - minY) / 2,
  };
};

/**
 * Calculates cursor position in SVG coordinates
 * @param e - Mouse event object
 * @param svgRef - Reference to SVG element
 * @returns Position object with x,y coordinates in SVG space
 */
export const calculateCursorPosition = (
  e: MouseEvent | React.MouseEvent,
  svgRef: React.RefObject<SVGSVGElement>
): Position2D | null => {
  if (!svgRef.current) return null;
  const svg = svgRef.current;

  // Create an SVGPoint and set its coordinates to the mouse position
  const point = svg.createSVGPoint();
  point.x = e.clientX;
  point.y = e.clientY;

  // Get the inverse of the current transformation matrix
  const ctm = svg.getScreenCTM()?.inverse();
  if (ctm) {
    // Transform the point to SVG coordinates
    const cursorPoint = point.matrixTransform(ctm);
    return { x: cursorPoint.x, y: cursorPoint.y };
  }

  return null;
};

/**
 * Calculates a selection rectangle from start and current position
 * @param startPosition - Starting position of selection
 * @param currentPosition - Current position of cursor
 * @returns Selection rectangle with left, right, top, bottom coordinates
 */
export const calculateSelectionRect = (
  startPosition: Position2D,
  currentPosition: Position2D
): SelectionRect => {
  return {
    left: Math.min(startPosition.x, currentPosition.x),
    right: Math.max(startPosition.x, currentPosition.x),
    top: Math.min(startPosition.y, currentPosition.y),
    bottom: Math.max(startPosition.y, currentPosition.y),
  };
};

/**
 * Determines if a point is inside a selection rectangle
 * @param point - Point to check
 * @param rect - Selection rectangle
 * @returns Boolean indicating if point is inside rectangle
 */
export const isPointInSelectionRect = (
  point: { x: number; y: number },
  rect: SelectionRect
): boolean => {
  return (
    point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
  );
};

/**
 * Snaps a position to a grid
 * @param position - Position to snap
 * @param gridSize - Size of grid cells
 * @returns Snapped position
 */
export const snapToGrid = (position: Position2D, gridSize: number): Position2D => {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  };
};

/**
 * Calculates distance between two points
 * @param p1 - First point
 * @param p2 - Second point
 * @returns Distance between points
 */
export const calculateDistance = (p1: Position2D, p2: Position2D): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculates angle between two points in degrees
 * @param p1 - First point
 * @param p2 - Second point
 * @returns Angle in degrees
 */
export const calculateAngle = (p1: Position2D, p2: Position2D): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (angle < 0) angle += 360;
  return angle;
};

/**
 * Minimum distance from a point to a line segment
 */
export const distancePointToSegment = (
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    // The segment is a point
    const ddx = px - x1;
    const ddy = py - y1;
    return Math.hypot(ddx, ddy);
  }
  // Project point onto the segment, clamped to [0,1]
  const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
  const tt = Math.max(0, Math.min(1, t));
  const cx = x1 + tt * dx;
  const cy = y1 + tt * dy;
  return Math.hypot(px - cx, py - cy);
};
